/**
 * billingSync.ts — WEB-936 п.5 (третье подтверждение QA 08.08, зонт WEB-1118).
 * WEB-1337 P6 (живой баг 15.08): две вкладки РАЗНЫХ организаций «зациклили»
 * обновление баланса — см. doc-comment у `broadcastBalanceSync` ниже для
 * разбора механизма и фикса.
 *
 * Кросс-вкладочная свежесть баланса/истории до сих пор держалась ТОЛЬКО на
 * поллинге (30с) + рефетче по visibilitychange/focus (useBillingAutoRefresh,
 * коммит 5978e214). Это закрывает сценарий «вернулся на вкладку», но не
 * закрывает «вкладка просто открыта рядом и её не покидали» — два окна
 * side-by-side, автоматизированный QA-прогон без реального user-focus:
 * document.hidden/window.focus в таком раскладе могут не измениться вовсе,
 * и единственный путь обновления — ждать до 30с интервала.
 *
 * Здесь — второй, НЕЗАВИСИМЫЙ от видимости/фокуса канал: браузерное
 * 'storage'-событие. Оно летит во ВСЕ прочие вкладки того же origin сразу же
 * при записи в localStorage, даже если вкладка document.hidden===true (в
 * отличие от setInterval/visibilitychange/focus). Каждая успешная загрузка
 * billing (fetchBillingThunk.fulfilled) широковещает актуальный баланс;
 * useBillingAutoRefresh слушает 'storage' в остальных вкладках и перезапрашивает
 * billing НЕМЕДЛЕННО, не дожидаясь фокуса/интервала.
 *
 * Значение из localStorage НЕ используется напрямую для отображения — оно
 * только сигнал «сходи перечитай billing»; авторитетный баланс всегда идёт с
 * сервера (fetchBillingThunk), поэтому таймингово-грязные записи безопасны.
 *
 * НЕ трогает списание/суммы/бэкенд — только момент, когда СОСЕДНЯЯ вкладка
 * узнаёт, что стоит перечитать уже существующий GET /billing.
 */

import {
  walletAvailableCents,
  walletHeldCents,
  type WalletFigures,
} from '@/utils/walletAvailable';

export const BILLING_SYNC_STORAGE_KEY = 'w2a.billingSync';

/**
 * WEB-2863: значение сигнала — «сумма порций|свободно|в резерве»; без полей
 * резерва (старый сервер) — одно число, как было.
 */
function syncValue(balanceCents: number, wallet?: WalletFigures): string {
  const hasHoldFields =
    wallet != null && (wallet.availableCents != null || wallet.heldCents != null);
  if (!hasHoldFields) return String(balanceCents);
  const figures = { ...wallet, balanceCents };
  return `${balanceCents}|${walletAvailableCents(figures) ?? balanceCents}|${walletHeldCents(figures)}`;
}
/**
 * WEB-1337 P6: отдельный ключ для organizationId, СВЯЗАННОГО с последним
 * широковещённым балансом. Держим его отдельно от BILLING_SYNC_STORAGE_KEY
 * (не сериализуем в JSON вместе) намеренно — на плоское числовое значение
 * основного ключа уже завязаны существующие тесты/потребители; добавление
 * второго ключа расширяет сигнал без изменения формата первого. Порядок
 * записи внутри broadcastBalanceSync важен: orgId пишется ПЕРЕД балансом, так
 * что к моменту, когда 'storage'-событие по основному ключу долетает до
 * соседней вкладки, актуальный orgId уже лежит в общем localStorage.
 */
export const BILLING_SYNC_ORG_STORAGE_KEY = 'w2a.billingSyncOrgId';

/**
 * Ф1 (ревизия десятки 3, живой баг 15.08 — пинг-понг «личная вкладка ↔
 * орг-вкладка»): значение, которое пишется в BILLING_SYNC_ORG_STORAGE_KEY,
 * когда broadcast идёт БЕЗ организации (личный режим). Раньше orgId=null
 * просто не писал орг-ключ вовсе — там мог остаться ЛЮБОЙ orgId от прошлого
 * широковещания (своего или соседней вкладки), и слушатель либо путал личную
 * вкладку с чужой организацией, либо (страхуясь про «неизвестно») рефетчил
 * всегда. Явный сентинел делает личный режим таким же различимым
 * идентификатором в общем ключе, как и любой orgId — сравнение у слушателя
 * становится строгим для ОБЕИХ сторон, не только для организаций.
 */
export const PERSONAL_BROADCAST_SENTINEL = 'personal';

/**
 * Широковещает актуальный баланс прочим вкладкам того же origin. Вызывать
 * после КАЖДОЙ успешной загрузки billing (не только при списании) — событие
 * 'storage' в браузере само не срабатывает при записи ТОГО ЖЕ значения, что
 * уже лежит в хранилище, так что повторные вызовы с НЕИЗМЕННЫМ балансом ВНУТРИ
 * ОДНОЙ организации не порождают лишних кросс-вкладочных рефетчей.
 *
 * WEB-1337 P6 (живой баг 15.08, «две вкладки зациклили баланс»): этой
 * дедупликации НЕДОСТАТОЧНО, когда вкладки принадлежат РАЗНЫМ организациям —
 * их балансы, как правило, РАЗНЫЕ числа, так что запись под общим ключом
 * меняется на каждый раунд и постоянно ретриггерит соседа: вкладка A (баланс
 * 100) пишет 100 → вкладка B видит 'storage', рефетчит СВОЙ (org B) баланс
 * 200, пишет 200 → вкладка A видит 'storage', рефетчит СВОЙ (org A) баланс
 * 100, пишет 100 → бесконечный пинг-понг, каждый раунд — реальный GET
 * /billing. Фикс: класть orgId рядом с балансом (см. BILLING_SYNC_ORG_STORAGE_KEY);
 * слушатель (useBillingAutoRefresh) рефетчит ТОЛЬКО когда broadcast-orgId
 * совпадает с активной организацией ЭТОЙ вкладки — соседняя-другая-орга
 * вкладка молча игнорирует чужой сигнал и не перетриггеривает цепочку.
 * `orgId` опционален для обратной совместимости с вызовами до этой правки.
 *
 * WEB-2863: сигнал несёт ещё свободный остаток и резерв идущих действий ИИ
 * (`wallet`). Резерв НЕ меняет сумму порций, а событие 'storage' браузер шлёт
 * только при изменившемся значении — из одной суммы соседняя вкладка о
 * резерве не узнавала (до 30 с), показывала старый свободный остаток и
 * разрешала трату, которую сервер отклонит. Числа берутся одной дверью
 * utils/walletAvailable.ts. Старый сервер без полей — прежняя форма (одно
 * число). Приёмник (useBillingAutoRefresh) значение не разбирает: любое
 * изменение = «перечитай /billing», срез обновляется настоящим ответом.
 */
export function broadcastBalanceSync(
  balanceCents: number,
  orgId: string | null = null,
  wallet?: WalletFigures,
): void {
  try {
    // Ф1: orgId=null тоже пишет орг-ключ теперь — сентинел PERSONAL_BROADCAST_SENTINEL,
    // а не пропуск записи (см. doc-comment у константы выше).
    window.localStorage.setItem(BILLING_SYNC_ORG_STORAGE_KEY, orgId ?? PERSONAL_BROADCAST_SENTINEL);
    window.localStorage.setItem(BILLING_SYNC_STORAGE_KEY, syncValue(balanceCents, wallet));
  } catch {
    // Приватный режим/переполненное хранилище — не повод ронять загрузку billing.
  }
}

/** Читает orgId (или сентинел PERSONAL_BROADCAST_SENTINEL для личного
 * режима) последнего широковещённого баланса. null — только если ключ ещё ни
 * разу не писался этой веткой кода (старый клиент до Ф1/WEB-1337 P6) или
 * localStorage недоступен; ЭТО единственный случай, который слушателю стоит
 * трактовать как «неизвестно, рефетчить безусловно». */
export function readBroadcastOrgId(): string | null {
  try {
    return window.localStorage.getItem(BILLING_SYNC_ORG_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Убирает сигнальный ключ. Вызывается при выходе и при входе под другим
 * аккаунтом (authSlice) — по правилу WEB-393: ничего от предыдущего
 * пользователя в браузере не остаётся. Само по себе число баланса никого не
 * называет, но это след его денег на общем компьютере.
 */
export function clearBalanceSync(): void {
  try {
    window.localStorage.removeItem(BILLING_SYNC_STORAGE_KEY);
    window.localStorage.removeItem(BILLING_SYNC_ORG_STORAGE_KEY);
  } catch {
    // Приватный режим — не повод ронять выход из аккаунта.
  }
}
